import { RoleEnum } from "@/enums";
import type {
    ApiBenchmarkEnvironmentEntryType,
    ApiBenchmarkListItemType,
    ApiBenchmarkRecommendedJobType,
    ApiBenchmarkResultType,
    ApiBenchmarkSelectedBranchType,
    ApiCompanyProfileType,
    ApiCompanyType,
    ApiInvitationType,
    ApiUserType,
    AuthUserType,
    BenchmarkEnvironmentEntryType,
    BenchmarkListItemType,
    BenchmarkRecommendedJobType,
    BenchmarkResultPayloadType,
    BenchmarkSelectedBranchType,
    CompanyProfileType,
    CompanyType,
    InvitationType,
} from "@/types";

export const transformApiUserHandler = ({
    company,
    company_id: companyId,
    company_name: companyName,
    created_at: createdAt,
    email,
    id,
    is_active: isActive,
    name,
    phone,
    updated_at: updatedAt,
    user_type: userType,
}: ApiUserType): AuthUserType => ({
    companyId,
    companyName: companyName ?? company?.name ?? undefined,
    createdAt,
    email,
    id,
    isActive,
    name,
    phone: phone ?? company?.phone ?? undefined,
    role: userType === RoleEnum.ADMIN ? RoleEnum.ADMIN : RoleEnum.COMPANY,
    updatedAt,
});

export const transformApiCompanyHandler = ({
    created_at: createdAt,
    disabled_at: disabledAt,
    email,
    id,
    is_enabled: isEnabled,
    name,
    phone,
    updated_at: updatedAt,
}: ApiCompanyType): CompanyType => ({
    createdAt,
    disabledAt,
    email,
    id,
    isEnabled: isEnabled ?? true,
    name,
    phone,
    updatedAt,
});

export const transformApiInvitationHandler = ({
    accepted_at: acceptedAtField,
    cancelled_at: cancelledAt,
    created_at: createdAt,
    email,
    expires_at: expiresAt,
    id,
    invited_by: invitedBy,
    registration_url: registrationUrl,
    status,
    used_at: usedAt,
}: ApiInvitationType): InvitationType => {
    const invitedByName = typeof invitedBy === "object" && invitedBy !== null ? invitedBy.name : undefined;

    return {
        acceptedAt: acceptedAtField ?? usedAt ?? null,
        cancelledAt: cancelledAt ?? null,
        createdAt,
        email,
        expiresAt,
        id,
        invitedByName,
        registrationUrl,
        status,
    };
};

export const transformApiCompanyProfileHandler = ({
    company,
    user,
}: ApiCompanyProfileType): CompanyProfileType => ({
    companyName: company?.name ?? user.company_name ?? user.name,
    email: user.email,
    name: user.name,
    phone: company?.phone ?? user.phone ?? "",
});

export const transformApiBenchmarkRecommendedJobHandler = ({
    job_title: jobTitle, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case response
    justification,
}: ApiBenchmarkRecommendedJobType): BenchmarkRecommendedJobType => ({
    jobTitle,
    justification,
});

export const transformApiBenchmarkSelectedBranchHandler = ({
    chosen_competencies: chosenCompetencies, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case response
    chosen_competencies_text: chosenCompetenciesText, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case response
    job_type: jobType, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case response
}: ApiBenchmarkSelectedBranchType): BenchmarkSelectedBranchType => ({
    chosenCompetencies: chosenCompetencies ?? [],
    chosenCompetenciesText: chosenCompetenciesText ?? [],
    jobType,
});

export const transformApiBenchmarkEnvironmentEntryHandler = ({
    answer_text: answerText, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case response
    question,
    selected_option: selectedOption, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case response
}: ApiBenchmarkEnvironmentEntryType): BenchmarkEnvironmentEntryType => ({
    answerText,
    question,
    selectedOption,
});

export const transformApiBenchmarkResultHandler = (apiResult: ApiBenchmarkResultType): BenchmarkResultPayloadType => ({
    environmentStatus: apiResult.environment_status?.map(transformApiBenchmarkEnvironmentEntryHandler),
    industry: apiResult.industry,
    jobTitle: apiResult.job_title,
    recommendedJobs: apiResult.recommended_jobs?.map(transformApiBenchmarkRecommendedJobHandler),
    selectedBranches: apiResult.selected_branches?.map(transformApiBenchmarkSelectedBranchHandler),
    seniority: apiResult.seniority,
});

export const transformApiBenchmarkListItemHandler = ({
    completed_at: completedAt, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    created_at: createdAt, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    current_step: currentStep, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    error_message: errorMessage, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    id,
    industry,
    job_description: jobDescription, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    job_id: jobId, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    job_title: jobTitle, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    progress,
    result,
    seniority,
    started_at: startedAt, // eslint-disable-line code-style/prop-naming-convention -- backend snake_case
    status,
}: ApiBenchmarkListItemType): BenchmarkListItemType => ({
    completedAt,
    createdAt,
    currentStep,
    errorMessage,
    id,
    industry,
    jobDescription,
    jobId,
    jobTitle,
    progress,
    recommendedJobs: result?.recommended_jobs?.map(transformApiBenchmarkRecommendedJobHandler),
    resultPayload: result ? transformApiBenchmarkResultHandler(result) : undefined,
    seniority,
    startedAt,
    status,
});
